import ButtonMUI from '@material-ui/core/Button';
import { createStyles, withStyles } from '@material-ui/styles';
import { border } from '@topconagriculture/theme';
import cn from 'classnames';
import * as React from 'react';
import { compose, defaultProps, pure } from 'recompose';
import { FontIcon } from '../FontIcon/';
import { Loader } from '@topconagriculture/loader';
export var ButtonVariants;
(function (ButtonVariants) {
    ButtonVariants["primary"] = "primary";
    ButtonVariants["secondary"] = "secondary";
    ButtonVariants["branded"] = "branded";
    ButtonVariants["tertiary"] = "tertiary";
    ButtonVariants["tertiaryOutlined"] = "tertiaryOutlined";
    ButtonVariants["warning"] = "warning";
    ButtonVariants["warningOutlined"] = "warningOutlined";
    ButtonVariants["error"] = "error";
    ButtonVariants["errorOutlined"] = "errorOutlined";
})(ButtonVariants || (ButtonVariants = {}));
export const styles = (theme) => createStyles({
    root: {
        width: '100%',
        maxWidth: 230,
        minHeight: 40,
        paddingTop: 7,
        paddingBottom: 7,
        borderRadius: theme.common.borderRadius.default,
        boxShadow: theme.common.shadow.light,
        fontWeight: theme.font.weight.bold,
        cursor: 'pointer',
        lineHeight: '1.4em',
    },
    autoWidth: {
        width: 'auto',
    },
    fullWidth: {
        maxWidth: '100%',
    },
    primary: {
        backgroundColor: theme.color.primary.main,
        color: theme.color.font.light,
        '&:hover': {
            backgroundColor: theme.color.primary.dark,
        },
        '&:active': {
            backgroundColor: theme.color.primary.dark,
        },
        '&$flat': {
            color: theme.color.primary.main,
        },
    },
    secondary: {
        backgroundColor: theme.color.background.light,
        border: border(theme.color.grey.light),
        color: theme.color.grey.darkest,
        '&:hover': {
            backgroundColor: theme.color.grey.light,
        },
        '&:active': {
            backgroundColor: theme.color.grey.light,
        },
    },
    tertiary: {
        backgroundColor: theme.color.grey.default,
        border: border(theme.color.grey.dark),
        color: theme.color.font.light,
        '&:hover': {
            backgroundColor: theme.color.grey.light,
        },
        '&:active': {
            backgroundColor: theme.color.grey.light,
        },
        '&$flat': {
            color: theme.color.grey.default,
        },
    },
    branded: {
        backgroundColor: theme.color.brand.main,
        color: theme.color.font.light,
        '&:hover': {
            backgroundColor: theme.color.brand.light,
        },
        '&:active': {
            backgroundColor: theme.color.brand.light,
        },
        '&$btnDisabled': {
            backgroundColor: theme.color.brand.main,
            color: theme.color.font.light,
            opacity: 0.45,
            '&$flat': {
                color: `${theme.color.brand.main} !important`,
                backgroundColor: 'transparent',
            },
        },
        '&$flat': {
            color: theme.color.brand.main,
        },
    },
    tertiaryOutlined: {
        border: border(theme.color.grey.dark),
        color: theme.color.grey.default,
        '&:hover, &:active': {
            backgroundColor: theme.color.grey.light,
            color: theme.color.font.dark,
        },
        '&$isActive': {
            boxShadow: `${theme.common.shadow.light}, inset 0 0 0 1px ${theme.color.grey.dark}`,
            backgroundColor: theme.color.grey.light,
        },
    },
    warning: {
        color: theme.color.font.light,
        backgroundColor: theme.color.warning.main,
        '&:hover, &:active': {
            backgroundColor: theme.color.warning.light,
            color: theme.color.font.light,
        },
        '&$isActive': {
            boxShadow: `${theme.common.shadow.light}, inset 0 0 0 1px ${theme.color.warning.main}`,
            backgroundColor: theme.color.warning.lightest,
        },
        '&$flat': {
            color: theme.color.warning.main,
        },
    },
    warningOutlined: {
        border: border(theme.color.warning.main),
        color: theme.color.grey.default,
        '&:hover, &:active': {
            backgroundColor: theme.color.warning.lightest,
            color: theme.color.font.dark,
        },
        '&$isActive': {
            boxShadow: `${theme.common.shadow.light}, inset 0 0 0 1px ${theme.color.warning.main}`,
            backgroundColor: theme.color.warning.lightest,
        },
        '&$flat': {
            color: theme.color.warning.main,
        },
    },
    error: {
        backgroundColor: theme.color.error.dark,
        color: theme.color.font.light,
        '&:hover, &:active': {
            backgroundColor: theme.color.error.light,
            color: theme.color.font.light,
        },
        '&$isActive': {
            boxShadow: `${theme.common.shadow.light}, inset 0 0 0 1px ${theme.color.error.main}`,
            backgroundColor: theme.color.error.lightest,
        },
        '&$flat': {
            color: theme.color.error.main,
        },
    },
    errorOutlined: {
        border: border(theme.color.error.main),
        color: theme.color.grey.default,
        '&:hover, &:active': {
            backgroundColor: theme.color.error.lightest,
            color: theme.color.font.dark,
        },
        '&$isActive': {
            boxShadow: `${theme.common.shadow.light}, inset 0 0 0 1px ${theme.color.error.main}`,
            backgroundColor: theme.color.error.lightest,
        },
        '&$flat': {
            color: theme.color.error.main,
        },
    },
    rounded: {
        borderRadius: 20,
    },
    isActive: {
        color: theme.color.font.dark,
    },
    flat: {
        backgroundColor: 'transparent',
        border: border('transparent'),
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
        '&:active': {
            backgroundColor: 'transparent',
        },
    },
    btnDisabled: {
        background: theme.color.grey.light,
        color: `${theme.color.grey.lightest} !important`,
        cursor: 'default',
        boxShadow: 'none',
        '&$flat': {
            backgroundColor: 'transparent',
            border: border('transparent'),
            boxShadow: 'none',
        },
    },
    buttonLoader: {
        color: 'white',
    },
    loading: {
        pointerEvents: 'none',
    },
    popoverButton: {
        marginTop: theme.space.margin.small,
    },
});
const Button = (props) => {
    const { label, classes, iconName, iconColor, iconSize, className: classNameProp, isLoading, autoWidth, fullWidth, flat, isActive, rounded, iconFromMUI, variant, testId = 'Button', ...other } = props;
    const rootClass = cn(classes.root, classNameProp);
    const iconEl = iconName && (React.createElement(FontIcon, { "data-testid": `${testId}-icon`, iconName: iconName, color: iconColor || 'white', size: iconSize || 22, top: 1, marginRight: label ? 12 : 0, fromMui: iconFromMUI }));
    const buttonClasses = cn({
        [classes[variant || ButtonVariants.primary]]: true,
        [classes.flat]: flat,
        [classes.loading]: isLoading,
        [classes.autoWidth]: autoWidth,
        [classes.fullWidth]: fullWidth,
        [classes.isActive]: isActive,
        [classes.rounded]: rounded,
    });
    const buttonLoaderClasses = cn({
        [classes.buttonLoader]: isLoading && variant === ButtonVariants.primary,
    });
    const renderLoading = isLoading && React.createElement(Loader, { size: 25, thickness: 3, className: buttonLoaderClasses });
    const renderLabel = (React.createElement(React.Fragment, null,
        iconEl,
        " ",
        label));
    return (React.createElement(ButtonMUI, Object.assign({ className: buttonClasses, classes: { root: rootClass, disabled: classes.btnDisabled }, "data-testid": testId }, other), isLoading ? renderLoading : renderLabel));
};
const ComposedButton = compose(pure, withStyles(styles, { withTheme: true }), defaultProps({
    role: 'button',
    flat: false,
    variant: ButtonVariants.primary,
    label: '',
    iconFromMUI: false,
}))(Button);
ComposedButton.displayName = 'Button';
export default ComposedButton;
//# sourceMappingURL=Button.js.map